import { create } from 'zustand';
import { User } from '../types';

interface AuthState {
  user: User | null;
  token: string | null;
  adminToken: string | null; // set when admin is impersonating another user
  adminUser: User | null;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  startImpersonation: (userToken: string, userObj: User) => void;
  exitImpersonation: () => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('token'),
  adminToken: localStorage.getItem('adminToken'),
  adminUser: null,
  setUser: (user) => set({ user }),
  setToken: (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    set({ token });
  },
  startImpersonation: (userToken, userObj) => {
    // Save the current admin token before switching
    const currentToken = localStorage.getItem('token');
    if (currentToken) localStorage.setItem('adminToken', currentToken);
    localStorage.setItem('token', userToken);
    set((state) => ({
      adminToken: currentToken,
      adminUser: state.user,
      token: userToken,
      user: userObj,
    }));
  },
  exitImpersonation: () => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('adminToken');
    }
    set((state) => ({
      token: adminToken,
      user: state.adminUser,
      adminToken: null,
      adminUser: null,
    }));
  },
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    set({ user: null, token: null, adminToken: null, adminUser: null });
  },
}));

interface UIState {
  isLoading: boolean;
  error: string | null;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  isLoading: false,
  error: null,
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
}));

interface ModalState {
  isOpen: boolean;
  modalType: 'admin-approval' | 'create-signal' | null;
  selectedUser: User | null;
  openModal: (type: 'admin-approval' | 'create-signal', user?: User) => void;
  closeModal: (type?: 'admin-approval' | 'create-signal') => void;
}

export const useModalStore = create<ModalState>((set) => ({
  isOpen: false,
  modalType: null,
  selectedUser: null,
  openModal: (type, user) => set({ isOpen: true, modalType: type, selectedUser: user || null }),
  closeModal: (type) => {
    if (type) {
      set((state) => {
        if (state.modalType === type) {
          return { isOpen: false, modalType: null, selectedUser: null };
        }
        return state;
      });
    } else {
      set({ isOpen: false, modalType: null, selectedUser: null });
    }
  },
}));

interface ConnectionBrokenAlertState {
  alert: {
    affectedUserId: string;
    affectedUserName: string;
    brokerageConnectionId: string;
    brokerageAuthorizationId: string;
    brokerageName: string;
    alertId: string;
  } | null;
  setAlert: (alert: ConnectionBrokenAlertState['alert']) => void;
  clearAlert: () => void;
}

export const useConnectionBrokenAlertStore = create<ConnectionBrokenAlertState>((set) => ({
  alert: null,
  setAlert: (alert) => set({ alert }),
  clearAlert: () => set({ alert: null }),
}));

